import { useRef } from "react";
import type { V2Position } from "@/hooks/useFloorPlanV2";

interface Props {
  canvasW: number;
  canvasH: number;
  positions: V2Position[];
  pan: { x: number; y: number };
  zoom: number;
  containerW: number;
  containerH: number;
  /** Recenter the viewport on the canvas point (cx, cy) in canvas px. */
  onRecenter: (cx: number, cy: number) => void;
}

const MAX_W = 140;
const MAX_H = 90;

export const V2Minimap = ({
  canvasW, canvasH, positions, pan, zoom, containerW, containerH, onRecenter,
}: Props) => {
  const ref = useRef<SVGSVGElement>(null);

  // Fit the canvas silhouette inside MAX_W × MAX_H, preserving ratio.
  const scale = Math.min(MAX_W / canvasW, MAX_H / canvasH);
  const w = Math.max(1, canvasW * scale);
  const h = Math.max(1, canvasH * scale);

  // Visible viewport (in canvas coords) → mini coords
  const visX = -pan.x / zoom;
  const visY = -pan.y / zoom;
  const visW = containerW / zoom;
  const visH = containerH / zoom;

  // Clamp viewport rect to mini bounds for display
  const rx = Math.max(0, Math.min(w, visX * scale));
  const ry = Math.max(0, Math.min(h, visY * scale));
  const rw = Math.max(2, Math.min(w - rx, visW * scale));
  const rh = Math.max(2, Math.min(h - ry, visH * scale));

  const handleClick = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    onRecenter(px / scale, py / scale);
  };

  return (
    <div
      className="absolute bottom-3 right-3 z-20 rounded-md border border-border bg-background/90 backdrop-blur shadow-sm p-1.5"
      data-no-drag
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <svg
        ref={ref}
        width={w}
        height={h}
        onClick={handleClick}
        className="block cursor-pointer"
        role="img"
        aria-label="Mini-plan"
      >
        <rect x={0} y={0} width={w} height={h} fill="hsl(var(--muted))" stroke="hsl(var(--border))" />
        {positions.map(p => (
          <rect
            key={p.tableId}
            x={p.x * scale}
            y={p.y * scale}
            width={Math.max(1, p.width * scale)}
            height={Math.max(1, p.height * scale)}
            fill="hsl(var(--muted-foreground) / 0.45)"
            rx={1}
          />
        ))}
        <rect
          x={rx}
          y={ry}
          width={rw}
          height={rh}
          fill="hsl(var(--primary) / 0.12)"
          stroke="hsl(var(--primary))"
          strokeWidth={1.5}
        />
      </svg>
    </div>
  );
};
